import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
    Rocket,
    Code2,
    Zap,
    Server,
    CheckCircle,
    Image as ImageIcon,
    Globe,
    Search,
    ArrowRight,
    Telescope
} from "lucide-react";

export default function Next() {
    const navigate = useNavigate();
    const features = [
        {
            icon: <Server className="w-6 h-6" />,
            title: "Server-Side Rendering",
            description: "Pages pré-rendues côté serveur pour un SEO optimal et des performances exceptionnelles"
        },
        {
            icon: <Zap className="w-6 h-6" />,
            title: "Optimisation Automatique",
            description: "Code splitting, lazy loading et optimisation d'images automatiques"
        },
        {
            icon: <Globe className="w-6 h-6" />,
            title: "Routes API Intégrées",
            description: "Backend et frontend dans le même projet pour une productivité maximale"
        },
        {
            icon: <Search className="w-6 h-6" />,
            title: "SEO Excellence",
            description: "Métadonnées dynamiques et rendu optimal pour les moteurs de recherche"
        }
    ];

    const useCases = [
        "Sites web d'entreprise avec fort enjeu SEO",
        "Applications e-commerce hautes performances",
        "Plateformes SaaS avec authentification complexe",
        "Sites de contenu et blogs professionnels",
        "Progressive Web Applications (PWA) avancées"
    ];

    const whyWeUse = [
        {
            title: "Production-Ready",
            description: "Framework React le plus utilisé en production, adopté par Vercel, Netflix, TikTok"
        },
        {
            title: "Developer Experience",
            description: "Hot reload instantané, TypeScript natif, outils de debugging avancés"
        },
        {
            title: "Performance Optimale",
            description: "Lighthouse scores de 100/100, temps de chargement ultra-rapides"
        }
    ];

    return (
        <div className="space-y-24">
            {/* Hero Section */}
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl border border-gray-700/50 p-12 md:p-16"
            >
                {/* Effet de fond animé */}
                <motion.div
                    animate={{
                        scale: [1, 1.3, 1],
                        opacity: [0.2, 0.4, 0.2]
                    }}
                    transition={{ duration: 10, repeat: Infinity }}
                    className="absolute top-0 left-0 w-96 h-96 bg-white/5 rounded-full blur-3xl"
                />

                <div className="relative z-10 grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.3, type: "spring" }}
                            className="inline-flex items-center gap-3 px-4 py-2 bg-white/10 rounded-full mb-6 border border-white/20"
                        >
                            <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
                            <span className="text-gray-300 font-semibold text-sm">Framework React Production</span>
                        </motion.div>

                        <h2 className="text-xl md:text-6xl font-bold text-white mb-6">
                            Next.js
                        </h2>

                        <p className="text-xl md:text-2xl text-gray-300 mb-6 font-semibold">
                            The React Framework for Production
                        </p>

                        <p className="text-gray-400 text-md md:text-lg leading-relaxed mb-8">
                            Next.js est le framework React le plus avancé pour créer des applications web full-stack.
                            Il combine le meilleur du rendu côté serveur, de la génération statique et du rendu côté client
                            pour offrir des performances exceptionnelles.
                        </p>

                        <div className="flex flex-wrap gap-3">
                            {["App Router", "Server Components", "Static Export", "Edge Runtime"].map((tag, i) => (
                                <motion.span
                                    key={i}
                                    initial={{ opacity: 0, scale: 0 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: 0.5 + i * 0.1 }}
                                    className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-gray-300 text-[12px] md:text-sm font-medium hover:bg-white/10 transition-colors"
                                >
                                    {tag}
                                </motion.span>
                            ))}
                        </div>
                    </div>

                    {/* Logo Next.js animé */}
                    <motion.div
                        className="flex items-center justify-center"
                    >
                        <div className="relative w-64 h-64 flex items-center justify-center">
                            {/* Cercles concentriques */}
                            {[1, 0.7, 0.4].map((scale, i) => (
                                <motion.div
                                    key={i}
                                    className="absolute inset-0 border border-white/20 rounded-full"
                                    style={{ scale }}
                                    animate={{
                                        scale: [scale, scale * 1.1, scale],
                                        opacity: [0.3, 0.6, 0.3]
                                    }}
                                    transition={{ duration: 3 + i, repeat: Infinity, delay: i * 0.5 }}
                                />
                            ))}

                            {/* Logo au centre */}
                            <motion.div
                                animate={{
                                    rotate: [0, 360],
                                }}
                                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                                className="relative z-10"
                            >
                                <div className="w-32 h-32 bg-white rounded-full flex items-center justify-center shadow-2xl shadow-white/20">
                                    <div className="text-black font-bold text-4xl">N</div>
                                </div>
                            </motion.div>

                            {/* Particules flottantes */}
                            {[...Array(8)].map((_, i) => (
                                <motion.div
                                    key={i}
                                    className="absolute w-2 h-2 bg-white/40 rounded-full"
                                    style={{
                                        top: `${50 + 40 * Math.sin(i * Math.PI / 4)}%`,
                                        left: `${50 + 40 * Math.cos(i * Math.PI / 4)}%`,
                                    }}
                                    animate={{
                                        y: [0, -20, 0],
                                        opacity: [0.4, 1, 0.4]
                                    }}
                                    transition={{ duration: 2, repeat: Infinity, delay: i * 0.25 }}
                                />
                            ))}
                        </div>
                    </motion.div>
                </div>
            </motion.div>

            {/* Points Forts */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
            >
                <h3 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-12 text-center">
                    Pourquoi<span className="bg-gradient-to-r from-gray-400 via-gray-300 to-gray-600 bg-clip-text text-transparent px-2 sm:px-4">Next.js</span>est Incontournable
                </h3>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {features.map((feature, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 * index }}
                            whileHover={{
                                scale: 1.05,
                                y: -5,
                                transition: { type: "spring", stiffness: 400 }
                            }}
                            className="bg-gradient-to-br from-gray-900/80 to-black/60 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50 hover:border-gray-500 transition-all duration-300 group"
                        >
                            <motion.div
                                whileHover={{ scale: 1.1, rotate: 5 }}
                                className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-white/20 group-hover:shadow-xl group-hover:shadow-white/10 transition-all duration-300"
                            >
                                <div className="text-gray-300">
                                    {feature.icon}
                                </div>
                            </motion.div>

                            <h4 className="text-xl font-bold text-white mb-2">
                                {feature.title}
                            </h4>
                            <p className="text-gray-400">
                                {feature.description}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            {/* Cas d'Usage */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="grid lg:grid-cols-2 gap-12"
            >
                <div>
                    <h3 className="text-3xl font-bold text-white mb-6">
                        Cas d'Usage Optimaux
                    </h3>
                    <div className="space-y-4">
                        {useCases.map((useCase, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.6 + index * 0.1 }}
                                className="flex items-start gap-3 group"
                            >
                                <motion.div
                                    whileHover={{ scale: 1.2 }}
                                    className="mt-1"
                                >
                                    <CheckCircle className="w-5 h-5 text-gray-400 group-hover:text-white flex-shrink-0 transition-colors" />
                                </motion.div>
                                <p className="text-gray-400 group-hover:text-white transition-colors">
                                    {useCase}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>

                <div>
                    <h3 className="text-3xl font-bold text-white mb-6">
                        Notre Expertise Next.js
                    </h3>
                    <div className="space-y-6">
                        {whyWeUse.map((reason, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.7 + index * 0.1 }}
                                whileHover={{ x: 10 }}
                                className="bg-gradient-to-r from-white/5 to-transparent backdrop-blur-sm rounded-xl p-6 border-l-4 border-white/30"
                            >
                                <h4 className="text-xl font-bold text-gray-200 mb-2">
                                    {reason.title}
                                </h4>
                                <p className="text-gray-400">
                                    {reason.description}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </motion.div>

            {/* Stack Complémentaire */}
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="bg-gradient-to-br from-gray-900/80 to-black/60 backdrop-blur-xl rounded-3xl p-12 border border-gray-700/50"
            >
                <h3 className="text-3xl font-bold text-white mb-8 text-center">
                    Notre Stack Next.js Enterprise
                </h3>

                <div className="grid md:grid-cols-3 gap-6">
                    {[
                        { name: "Prisma", desc: "ORM TypeScript moderne", color: "from-gray-600 to-gray-800" },
                        { name: "NextAuth.js", desc: "Authentification complète", color: "from-purple-600 to-indigo-600" },
                        { name: "Vercel", desc: "Déploiement optimisé", color: "from-black to-gray-900" },
                        { name: "tRPC", desc: "API type-safe end-to-end", color: "from-blue-600 to-cyan-600" },
                        { name: "Shadcn/ui", desc: "Composants UI premium", color: "from-gray-700 to-gray-900" },
                        { name: "Stripe", desc: "Paiements modernes", color: "from-purple-500 to-purple-700" }
                    ].map((tool, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.9 + index * 0.05 }}
                            whileHover={{ scale: 1.05 }}
                            className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 hover:border-gray-500 transition-all duration-300"
                        >
                            <div className={`h-2 w-16 bg-gradient-to-r ${tool.color} rounded-full mb-4`} />
                            <h4 className="text-lg font-bold text-white mb-2">{tool.name}</h4>
                            <p className="text-gray-400 text-sm">{tool.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            {/* CTA */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                className="text-center flex flex-col items-center justify-center bg-gradient-to-r from-gray-900/60 to-black/60 backdrop-blur-xl rounded-3xl p-12 border border-gray-700/50"
            >
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                    Propulsez Votre Projet avec Next.js
                </h3>
                <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
                    Bénéficiez de notre expertise Next.js pour créer une application performante et scalable
                </p>
                <motion.button
                    onClick={() => navigate("/Contact")}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-[250px] sm:w-[320px] cursor-pointer px-6 py-4 sm:px-8 sm:py-5 text-center text-[14px] sm:text-lg font-medium flex items-center gap-1 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold rounded-xl shadow-xl hover:shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 cursor-pointer"
                >
                    <span>Démarrer un projet Next.js</span>
                    <Telescope className="w-5 h-5 md:w-6 md:h-6" />
                </motion.button>
            </motion.div>
        </div>
    );
}
